/**
 * @license Copyright (c) Microsoft Corporation.  All rights reserved.
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { FormRender } from "./FormRender";
import { JsonFormSerializationBuilder } from "./JsonFormSerializationBuilder";
import { FormSerializer } from "./FormSerializer";
import { LookupControlFactory } from "./Lookups/LookupControlFactory";
import { FormValidation } from "./Validation/FormValidation";
import { MultiSelectValidator } from "./Validation/MultiSelectValidator";
import { HtmlAttributes } from "../../../Common/Constants/HtmlAttributes";
import { createFormSubmitEvent } from "./Events/CreateFormSubmitEvent";
import { createAfterFormSubmitFailEvent, createAfterFormSubmitSuccessEvent } from "./Events/CreateAfterFormSubmitEvent";
import { createAfterFormLoadEvent } from "./Events/CreateAfterFormLoadEvent";
import { CaptchaConfigurationConstants } from "../../../Common/Captcha/Captcha";
import { Captcha } from "./Captcha/Captcha";
import { findCaptchaBlock } from "./Utils/CaptchaUtils";
import { Guid } from "@contenteditor-shared/content-editor-common/src/Utils";
import { FormSubmissionErrorMessage } from "./Constants/FormSubmission";
import { CaptchaElements } from "./Constants/CaptchaConstants";
import { TopicValidator } from "./Validation/TopicValidator";
import { FormSubmitStatus } from "./Constants/FormStatus";
import { ErrorFeedback, ReloadFeedback, createCorsErrorFeedback, showFeedback, showRenderingFeedback, showSubmissionFeedback } from "./Feedback";
import { doesPhoneNumberStartWithCountryCode } from "./Utils/HtmlUtils";
import { SessionsValidator } from "./EventManagement/Validation";
import { EventFormSerializer } from "./EventManagement/FormSerialization";
import { SubmissionFeedbackProvider } from "./Feedback/SubmissionFeedbackProvider";
import { createFormSubmissionCookie } from "./Utils/FormSubmissionCookieUtils";
var FormLoader = /** @class */ (function () {
    function FormLoader(logger, formServiceClient, translate) {
        this.logger = logger;
        this.formServiceClient = formServiceClient;
        this.translate = translate;
    }
    FormLoader.prototype.loadForm = function (form) {
        return __awaiter(this, void 0, void 0, function () {
            var formResponse, e_1, feedback;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (form === null || form === void 0 ? void 0 : form.Error) {
                            showFeedback(__assign(__assign({}, ErrorFeedback), { message: form.Error }), form);
                            return [2 /*return*/];
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 4, 5, 6]);
                        FormRender.loadForm(form);
                        return [4 /*yield*/, this.formServiceClient.getFormByFormUrl(form.FormUrl, form.FormLoadResponseType)];
                    case 2:
                        formResponse = _a.sent();
                        FormRender.renderForm(form, formResponse.formHtml, formResponse.formRenderingStatus);
                        return [4 /*yield*/, this.registerHandlersToForm(form)];
                    case 3:
                        _a.sent();
                        showRenderingFeedback(formResponse.formRenderingStatus, form);
                        // we don't await here intentionally - we don't want to wait until form visit is created.
                        this.sendFormVisitInteraction(form);
                        form.FormPlaceholders.forEach(function (placeholder) { return placeholder.Element.dispatchEvent(createAfterFormLoadEvent()); });
                        return [3 /*break*/, 6];
                    case 4:
                        e_1 = _a.sent();
                        this.logger.error(e_1.message);
                        feedback = __assign(__assign({}, ReloadFeedback), { message: "FormFailedToLoad" });
                        if (e_1.name === "TypeError" && e_1.message === "Failed to fetch") {
                            feedback = createCorsErrorFeedback(form, this.formServiceClient);
                        }
                        showFeedback(feedback, form);
                        return [3 /*break*/, 6];
                    case 5:
                        FormRender.removeLoader(form);
                        return [7 /*endfinally*/];
                    case 6: return [2 /*return*/];
                }
            });
        });
    };
    FormLoader.prototype.registerHandlersToForm = function (form) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this.attachSubmitHandlers(form);
                        return [4 /*yield*/, Promise.all([this.attachValidationHandlers(form), this.initCaptcha(form)])];
                    case 1:
                        _a.sent();
                        this.attachPhoneFieldHandlers(form);
                        this.attachLookupsAutosuggest(form);
                        return [2 /*return*/];
                }
            });
        });
    };
    FormLoader.prototype.sendFormVisitInteraction = function (form) {
        return __awaiter(this, void 0, void 0, function () {
            var url, payload, ex_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (form.DoNotTrack === true) {
                            return [2 /*return*/];
                        }
                        url = window.location.href;
                        payload = { pageUrl: url };
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this.formServiceClient.sendFormVisit(form.FormVisitUrl, payload)];
                    case 2:
                        _a.sent();
                        return [3 /*break*/, 4];
                    case 3:
                        ex_1 = _a.sent();
                        this.logger.error(ex_1.message);
                        showFeedback(createCorsErrorFeedback(form, this.formServiceClient), form);
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    FormLoader.prototype.getFormSubmitProperties = function (form, formElement) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
        return __awaiter(this, void 0, void 0, function () {
            var _m, _o, _p;
            var _q;
            return __generator(this, function (_r) {
                switch (_r.label) {
                    case 0:
                        _q = {};
                        if (!((_c = (_b = (_a = form.FormSubmitProperties) === null || _a === void 0 ? void 0 : _a.SuccessMessage) !== null && _b !== void 0 ? _b : formElement.getAttribute(HtmlAttributes.successMessage)) !== null && _c !== void 0)) return [3 /*break*/, 1];
                        _m = _c;
                        return [3 /*break*/, 3];
                    case 1: return [4 /*yield*/, this.translate("FormSubmitted")];
                    case 2:
                        _m = (_r.sent());
                        _r.label = 3;
                    case 3:
                        _q.SuccessMessage = _m;
                        if (!((_f = (_e = (_d = form.FormSubmitProperties) === null || _d === void 0 ? void 0 : _d.WaitlistMessage) !== null && _e !== void 0 ? _e : formElement.getAttribute(HtmlAttributes.waitlistMessage)) !== null && _f !== void 0)) return [3 /*break*/, 4];
                        _o = _f;
                        return [3 /*break*/, 6];
                    case 4: return [4 /*yield*/, this.translate("FormSubmittedWaitlist")];
                    case 5:
                        _o = (_r.sent());
                        _r.label = 6;
                    case 6:
                        _q.WaitlistMessage = _o;
                        if (!((_j = (_h = (_g = form.FormSubmitProperties) === null || _g === void 0 ? void 0 : _g.ErrorMessage) !== null && _h !== void 0 ? _h : formElement.getAttribute(HtmlAttributes.errorMessage)) !== null && _j !== void 0)) return [3 /*break*/, 7];
                        _p = _j;
                        return [3 /*break*/, 9];
                    case 7: return [4 /*yield*/, this.translate("FormSubmitError")];
                    case 8:
                        _p = (_r.sent());
                        _r.label = 9;
                    case 9: return [2 /*return*/, (_q.ErrorMessage = _p,
                            _q.RedirectUrl = (_l = (_k = form.FormSubmitProperties) === null || _k === void 0 ? void 0 : _k.RedirectUrl) !== null && _l !== void 0 ? _l : formElement.getAttribute(HtmlAttributes.redirectUrl),
                            _q.PreventSubmissionUi = form.FormSubmitProperties.PreventSubmissionUi || formElement.getAttribute(HtmlAttributes.preventSubmissionUi) === "true",
                            _q)];
                }
            });
        });
    };
    FormLoader.prototype.attachSubmitHandlers = function (form) {
        var _this = this;
        form.FormPlaceholders.forEach(function (placeholder) {
            var allForms = placeholder.Element.querySelectorAll("form");
            var _loop_1 = function (i) {
                var currentForm = allForms[i];
                var showFormSubmissionFeedback = function (submissionStatus) {
                    showSubmissionFeedback(submissionStatus, currentForm);
                };
                currentForm.addEventListener("submit", function (e) { return __awaiter(_this, void 0, void 0, function () { return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0: return [4 /*yield*/, this.handleFormSubmission(e, form, currentForm, placeholder, showFormSubmissionFeedback)];
                        case 1: return [2 /*return*/, _a.sent()];
                    }
                }); }); });
            };
            for (var i = 0; i < allForms.length; i++) {
                _loop_1(i);
            }
        });
    };
    FormLoader.prototype.handleFormSubmission = function (event, form, currentForm, placeholder, showFormSubmissionFeedback) {
        return __awaiter(this, void 0, void 0, function () {
            var formSubmitProps, hasRedirectUrl;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        event.preventDefault();
                        return [4 /*yield*/, this.getFormSubmitProperties(form, currentForm)];
                    case 1:
                        formSubmitProps = _a.sent();
                        hasRedirectUrl = !!formSubmitProps.RedirectUrl;
                        return [4 /*yield*/, this.withDisabledSubmitButtons(currentForm, hasRedirectUrl, function () { return __awaiter(_this, void 0, void 0, function () {
                                var payloadObject, payload, cancelled, response, _a;
                                return __generator(this, function (_b) {
                                    switch (_b.label) {
                                        case 0:
                                            payloadObject = null;
                                            this.setCaptchaFields(placeholder.Element);
                                            payload = this.serializeForm(form, currentForm);
                                            if ((payload === null || payload === void 0 ? void 0 : payload.data) && payload.data.length) {
                                                payloadObject = JSON.parse(payload === null || payload === void 0 ? void 0 : payload.data);
                                            }
                                            cancelled = !currentForm.dispatchEvent(createFormSubmitEvent(payloadObject));
                                            if (cancelled) {
                                                return [2 /*return*/];
                                            }
                                            _b.label = 1;
                                        case 1:
                                            _b.trys.push([1, 4, , 5]);
                                            return [4 /*yield*/, this.formServiceClient.submitForm(form.SubmitFormUrl, payload.data)];
                                        case 2:
                                            response = _b.sent();
                                            return [4 /*yield*/, this.handleFormSubmissionResponse(response, form, currentForm, payloadObject, formSubmitProps, showFormSubmissionFeedback)];
                                        case 3:
                                            _b.sent();
                                            return [3 /*break*/, 5];
                                        case 4:
                                            _a = _b.sent();
                                            currentForm.dispatchEvent(createAfterFormSubmitFailEvent(payloadObject));
                                            if (!formSubmitProps.PreventSubmissionUi) {
                                                showFormSubmissionFeedback(__assign(__assign({}, ErrorFeedback), { message: formSubmitProps.ErrorMessage || ErrorFeedback.message }));
                                            }
                                            return [3 /*break*/, 5];
                                        case 5: return [2 /*return*/];
                                    }
                                });
                            }); })];
                    case 2:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    FormLoader.prototype.handleFormSubmissionResponse = function (response, form, currentForm, payloadObject, formSubmitProps, showFormSubmissionFeedback) {
        var _a;
        return __awaiter(this, void 0, void 0, function () {
            var WLSPHIP0, submissionStatus, eventDispatched, successFeedback;
            return __generator(this, function (_b) {
                if (response.submissionStatus != "OK" && response.submissionStatus != FormSubmitStatus.Success) {
                    if (response.submissionStatus == FormSubmitStatus.ValidationError &&
                        ((_a = response.errorMessage) === null || _a === void 0 ? void 0 : _a.toLowerCase().includes(FormSubmissionErrorMessage.CaptchaVerificationFailed.toLowerCase()))) {
                        WLSPHIP0 = window.WLSPHIP0;
                        // captcha verification failed
                        WLSPHIP0.reloadCaptchaAndShowError(1);
                        return [2 /*return*/];
                    }
                    currentForm.dispatchEvent(createAfterFormSubmitFailEvent(payloadObject));
                    if (!formSubmitProps.PreventSubmissionUi) {
                        submissionStatus = typeof response.submissionStatus === "string"
                            ? __assign(__assign({}, ErrorFeedback), { message: response.errorMessage || ErrorFeedback.message }) : response.submissionStatus;
                        showFormSubmissionFeedback(submissionStatus);
                    }
                    return [2 /*return*/];
                }
                createFormSubmissionCookie(response.formSubmissionId, this.logger);
                eventDispatched = false;
                if (formSubmitProps.RedirectUrl) {
                    currentForm.dispatchEvent(createAfterFormSubmitSuccessEvent(payloadObject));
                    eventDispatched = true;
                    if (this.redirectTo(formSubmitProps.RedirectUrl)) {
                        return [2 /*return*/];
                    }
                    else {
                        this.setSubmitButtonsState(currentForm, true);
                    }
                }
                if (!formSubmitProps.PreventSubmissionUi) {
                    successFeedback = SubmissionFeedbackProvider.getSuccessFeedback(form, currentForm, formSubmitProps);
                    showFormSubmissionFeedback(successFeedback);
                }
                if (!eventDispatched) {
                    currentForm.dispatchEvent(createAfterFormSubmitSuccessEvent(payloadObject));
                }
                return [2 /*return*/];
            });
        });
    };
    FormLoader.prototype.redirectTo = function (redirectUrl) {
        // when iframe hosting is implemented, must use window.parent.location.href for that case
        try {
            window.location.href = redirectUrl;
            return true;
        }
        catch (e) {
            console.error("Form redirect issue", e);
            return false;
        }
    };
    FormLoader.prototype.attachValidationHandlers = function (form) {
        return __awaiter(this, void 0, void 0, function () {
            var validationConfig;
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _a = {};
                        return [4 /*yield*/, this.translate("ValidationRequiredField")];
                    case 1:
                        _a.fieldRequiredErrorMessage = _b.sent();
                        return [4 /*yield*/, this.translate("ValidationSessionSelectionRequired")];
                    case 2:
                        _a.sessionRegistrationRequiredErrorMessage = _b.sent();
                        return [4 /*yield*/, this.translate("ValidationSingleSessionSelectionRequired")];
                    case 3:
                        validationConfig = (_a.singleSessionRegistrationRequiredErrorMessage = _b.sent(),
                            _a);
                        form.FormPlaceholders.forEach(function (placeholder) {
                            var allForms = placeholder.Element.querySelectorAll("FORM");
                            for (var i = 0; i < allForms.length; i++) {
                                var currentForm = allForms[i];
                                FormLoader.validation.attachValidation(currentForm, validationConfig);
                            }
                        });
                        return [2 /*return*/];
                }
            });
        });
    };
    FormLoader.prototype.attachPhoneFieldHandlers = function (form) {
        var _this = this;
        form.FormPlaceholders.forEach(function (placeholder) {
            var phoneFormFieldBlocks = placeholder.Element.querySelectorAll("DIV[".concat(HtmlAttributes.blockType, "=\"PhoneFormField\"]"));
            var _loop_2 = function (i) {
                var input = phoneFormFieldBlocks[i].querySelector('INPUT[type="tel"]');
                if (input) {
                    // Init hide country code action if value was prefilled
                    _this.hidePhoneCountryCodeField(phoneFormFieldBlocks[i], input.value);
                    input.addEventListener("input", function () {
                        _this.hidePhoneCountryCodeField(phoneFormFieldBlocks[i], input.value);
                    });
                }
            };
            for (var i = 0; i < phoneFormFieldBlocks.length; i++) {
                _loop_2(i);
            }
        });
    };
    FormLoader.prototype.hidePhoneCountryCodeField = function (phoneFormFieldBlock, inputValue) {
        var span = phoneFormFieldBlock.querySelector(".phoneCountryCode span");
        var countryCode = phoneFormFieldBlock.getAttribute(HtmlAttributes.dataCountryCode);
        if (!span) {
            return;
        }
        if (!countryCode || countryCode === "+" || doesPhoneNumberStartWithCountryCode(inputValue)) {
            span.hidden = true;
        }
        else {
            span.hidden = false;
        }
    };
    FormLoader.prototype.setCaptchaFields = function (placeholder) {
        var captchaBlock = findCaptchaBlock([placeholder]);
        if (!captchaBlock) {
            return;
        }
        var solutionValue = captchaBlock.querySelector("[id^=".concat(CaptchaConfigurationConstants.captchaInputIdPrefix, "]")).value;
        var typeValue = window.WLSPHIP0.type;
        captchaBlock.querySelector("#".concat(CaptchaElements.solutionId)).value = solutionValue;
        captchaBlock.querySelector("#".concat(CaptchaElements.typeId)).value = typeValue;
    };
    FormLoader.prototype.initCaptcha = function (form) {
        return __awaiter(this, void 0, void 0, function () {
            var captchaBlock, scriptHolder, captchaLanguage, captchaFlowId, captchaType, captchaRnd, captchaScriptUrl, renderScript, captchaFlowIdElement;
            return __generator(this, function (_a) {
                captchaBlock = findCaptchaBlock(form.FormPlaceholders.map(function (ph) { return ph.Element; }));
                if (!captchaBlock) {
                    return [2 /*return*/];
                }
                scriptHolder = captchaBlock.querySelector("#".concat(CaptchaElements.hipScriptId));
                if (scriptHolder == null) {
                    return [2 /*return*/];
                }
                captchaLanguage = captchaBlock.getAttribute(HtmlAttributes.captchaLanguage);
                if (!captchaLanguage) {
                    return [2 /*return*/];
                }
                captchaFlowId = Guid.new.toString();
                captchaType = "visual";
                captchaRnd = Guid.new.toString();
                captchaScriptUrl = "".concat(form.FormApiBaseUrl, "/captcha/script?fid=").concat(captchaFlowId, "&type=").concat(captchaType, "&rnd=").concat(captchaRnd, "&mkt=").concat(captchaLanguage);
                window.WLSPHIP0 = new Captcha();
                renderScript = document.createElement("script");
                renderScript.src = captchaScriptUrl;
                scriptHolder.insertBefore(renderScript, scriptHolder.firstChild);
                captchaFlowIdElement = captchaBlock.querySelector("#".concat(CaptchaElements.captchaFlowId));
                if (captchaFlowIdElement != null) {
                    captchaFlowIdElement.value = captchaFlowId;
                }
                return [2 /*return*/];
            });
        });
    };
    FormLoader.prototype.serializeForm = function (form, formElement) {
        var serializer = new JsonFormSerializationBuilder(window.location.href);
        FormSerializer.serialize(formElement, serializer);
        EventFormSerializer.serialize(form, formElement, serializer);
        return serializer.build();
    };
    FormLoader.prototype.withDisabledSubmitButtons = function (form, hasRedirectUrl, callback) {
        return __awaiter(this, void 0, void 0, function () {
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this.setSubmitButtonsState(form, false);
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, , 3, 4]);
                        return [4 /*yield*/, callback()];
                    case 2:
                        _a.sent();
                        return [3 /*break*/, 4];
                    case 3:
                        if (!hasRedirectUrl) {
                            this.setSubmitButtonsState(form, true);
                        }
                        else {
                            setTimeout(function () {
                                _this.setSubmitButtonsState(form, true);
                            }, 5000);
                        }
                        return [7 /*endfinally*/];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    FormLoader.prototype.setSubmitButtonsState = function (form, enabled) {
        Array.from(form.querySelectorAll("[type='submit']")).forEach(function (button) {
            if (!enabled) {
                button["d365mktSavedInnerHTML"] = button.innerHTML;
            }
            button.disabled = !enabled;
            button.innerHTML = enabled ? button["d365mktSavedInnerHTML"] || '<span> Submit </span>' : '<div class="submitButtonSpinner">';
            button["d365mktSavedInnerHTML"] = null;
        });
    };
    FormLoader.prototype.attachLookupsAutosuggest = function (form) {
        var _this = this;
        form.FormPlaceholders.forEach(function (placeholder) {
            var allLookups = placeholder.Element.querySelectorAll("INPUT[list]");
            for (var i = 0; i < allLookups.length; i++) {
                var element = allLookups[i];
                LookupControlFactory.CreateLookupControl(form, element, _this.logger);
            }
        });
    };
    FormLoader.validation = new FormValidation([new MultiSelectValidator(), new TopicValidator(), new SessionsValidator()]);
    return FormLoader;
}());
export { FormLoader };
